import { LocalizedText, showSuccessAlert, useDialog } from '@mews/framework';
import { Enterprise, Order, useRepositoryMutation } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import {
    Button,
    IconName,
    Table,
    ActionToolbarButton,
    ActionToolbar,
    TableHeader,
    TableProvider,
    usePaginatedData,
    useRowSelection,
    useTableState,
} from '@optimus-web/core';
import { useIsUserProvisioned } from '../../../hooks/useIsUserProvisioned';
import { useUserProfilesGet } from '../../../queries';
import { AssignPropertiesDialog } from '../AssignProperties';
import { TooltipWrapper } from '../../TooltipWrapper';
import { useInvalidateUserProfile } from '../../../hooks/useInvalidateUserProfile';
import { useColumns } from './tableMapping';
import { getFilteredProperties, usePropertiesUtils } from './utils';

export const EditUserProperties = ({ userId, isReadOnly = false }: { userId: string; isReadOnly?: boolean }) => {
    const {
        hasRowsSelected,
        resetRowSelection,
        rowSelection,
        selectedRowsCount,
        selectedRowsIds,
        setRowSelection,
        unselectRow,
    } = useRowSelection();

    const invalidateUserProfile = useInvalidateUserProfile();

    const [tableState, setTableState] = useTableState<Enterprise>({
        sort: { name: { order: Order.Ascending, priority: 0 } },
    });

    const [assignPropertiesDialogProps, toggleAssignPropertiesDialog] = useDialog();

    const detailedUser = useUserProfilesGet(userId);

    const isEditDisabled = useIsUserProvisioned(detailedUser.data) || isReadOnly;

    const unassignProperties = useRepositoryMutation('userProfiles', 'updateProperties', {
        onSuccess: () => {
            resetRowSelection();
            invalidateUserProfile(userId);

            showSuccessAlert({
                description: <LocalizedText textKey={TextKey.UserProfileUnassignPropertiesSuccessMessageDescription} />,
                title: <LocalizedText textKey={TextKey.UserProfileUnassignPropertiesSuccessMessageTitle} />,
            });
        },
    });

    const assignedProperties = detailedUser.data?.enterprises || [];

    const { isNoPropertyLeft, areAllPropertiesAssigned } = usePropertiesUtils({
        assignedProperties,
        selectedRowsCount,
    });

    const { data: visibleProperties, totalRowCount } = usePaginatedData(
        getFilteredProperties({
            data: assignedProperties,
            searchTerm: tableState.globalFilters.search,
            sortOrder: tableState.sort.name?.order,
        }),
        tableState.pagination
    );

    const columns = useColumns({
        onRowDelete: unselectRow,
        removalDisabled: isNoPropertyLeft,
        userId,
        isReadOnly: isEditDisabled,
    });

    return (
        <>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
                <TableHeader
                    title={<LocalizedText textKey={TextKey.PortfolioPropertyPlural} />}
                    subTitle={totalRowCount.toString()}
                    globalSearch={{ placeholder: <LocalizedText textKey={TextKey.PropertiesSearchHint} /> }}
                    rightSide={
                        !isEditDisabled && (
                            <TooltipWrapper
                                active={areAllPropertiesAssigned}
                                tooltip={<LocalizedText textKey={TextKey.UserProfileAssignPropertiesButtonTooltip} />}
                            >
                                <Button
                                    disabled={areAllPropertiesAssigned}
                                    icon={IconName.PlusSimple}
                                    variant="tertiary"
                                    onClick={toggleAssignPropertiesDialog}
                                    type="submit"
                                >
                                    <LocalizedText textKey={TextKey.Assign} />
                                </Button>
                            </TooltipWrapper>
                        )
                    }
                />
                {!isEditDisabled && (
                    <ActionToolbar
                        disabled={!hasRowsSelected}
                        leftSide={
                            hasRowsSelected ? (
                                <LocalizedText
                                    textKey={TextKey.SelectedItems}
                                    substitutes={{ count: selectedRowsCount }}
                                />
                            ) : null
                        }
                        rightSide={
                            <TooltipWrapper
                                active={isNoPropertyLeft}
                                tooltip={
                                    <LocalizedText textKey={TextKey.UserProfileBulkDeletePropertiesButtonTooltip} />
                                }
                            >
                                <ActionToolbarButton
                                    icon={IconName.Delete}
                                    disabled={!hasRowsSelected || isNoPropertyLeft}
                                    onClick={() =>
                                        unassignProperties.mutate({
                                            enterpriseIds: selectedRowsIds,
                                            userId,
                                            unassign: true,
                                        })
                                    }
                                >
                                    <LocalizedText textKey={TextKey.Remove} />
                                </ActionToolbarButton>
                            </TooltipWrapper>
                        }
                    />
                )}
                <Table
                    amountOfRows={totalRowCount}
                    data={visibleProperties}
                    loading={detailedUser.isPending || unassignProperties.isPending}
                    rowSelection={
                        isEditDisabled
                            ? undefined
                            : {
                                  rowSelection,
                                  onRowSelectionChange: setRowSelection,
                              }
                    }
                    uniqueRowId="id"
                />
            </TableProvider>

            <AssignPropertiesDialog userId={userId} {...assignPropertiesDialogProps} />
        </>
    );
};
